/* © CNES */
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { useMemo, useState } from 'react';
import { AssetWithProductId } from '../../../../../models/CollectionDetails';
import './ShowFilesToDownloadDialog.scss';

/**
 * Asset Shown interface.
 */
interface AssetShown {
  href: string;
  title: string;
  checked: boolean;
  productId: string;
}

/**
 * Show Files To Download Dialog.
 * @returns ShowFilesToDownload Dialog Element.
 */
export const ShowFilesToDownloadDialog = ({
  assetsFromProduct,
  setAssetsToDownload,
  showDialog,
  setShowDialog,
}: {
  assetsFromProduct: AssetWithProductId[];
  setAssetsToDownload: Function;
  showDialog: boolean;
  setShowDialog: Function;
}) => {
  const assetsToShow = useMemo(() => {
    return assetsFromProduct.map((asset: AssetWithProductId) => {
      return {
        href: asset.href,
        title: asset.title,
        checked: false,
        productId: asset.productId,
      };
    });
  }, [assetsFromProduct]);
  const [assetsSelected, setAssetsSelected] = useState<AssetShown[]>([]);

  /**
   * toggle Assets elected.
   * @param title asset title to toggle.
   */
  const toggleAssetsSelected = (title: string) => {
    const assetIndexToToggle = assetsToShow.findIndex(
      (asset) => asset.title === title
    );
    assetsToShow[assetIndexToToggle].checked =
      !assetsToShow[assetIndexToToggle].checked;
    setAssetsSelected(assetsToShow.filter((asset) => asset.checked === true));
  };

  /**
   * handle Deselect All Items set SelectedJobs to empty array.
   */
  const handleDeselectAllItems = () => {
    setAssetsSelected([]);
  };

  /**
   * handle Click on Download
   */
  const handleClickOnDownload = () => {
    setAssetsToDownload(assetsSelected);
    handleDeselectAllItems();
    setShowDialog(false);
  };

  /**
   * handle Select All Items.
   */
  const handleSelectAllItems = () => {
    setAssetsSelected(assetsToShow);
  };

  return (
    <Dialog
      header={'Multiple assets available to download'}
      headerStyle={{
        lineHeight: '10px',
        color: '#002d80',
      }}
      resizable={false}
      visible={showDialog}
      onHide={() => {
        handleDeselectAllItems();
        setShowDialog(false);
      }}
      className="download-dialog"
    >
      <div className="download-dialog-body">
        <div className="download-select-buttons-container">
          <Button
            label="Select all"
            className="button-select"
            onClick={handleSelectAllItems}
            link
          />
          <Button
            label="Deselect all"
            className="button-select"
            onClick={handleDeselectAllItems}
            link
          />
        </div>
        <div className="download-dialog-items">
          {assetsToShow?.map((asset) => {
            return (
              <div className="download-item" key={asset.title}>
                <div
                  className="download-checkbox"
                  data-testid="checkbox-container"
                >
                  <Checkbox
                    inputId={asset.title}
                    value={asset.title}
                    onChange={() => toggleAssetsSelected(asset.title)}
                    checked={assetsSelected.some(
                      (assetShown) => assetShown.title === asset.title
                    )}
                    className="download-checkbox"
                  />
                </div>
                <div
                  aria-label="title-container"
                  className={`download-title ${
                    assetsSelected.some(
                      (assetShown) => assetShown.title === asset.title
                    ) && 'checked'
                  }`}
                  onClick={() => toggleAssetsSelected(asset.title)}
                >
                  <p>{asset.title}</p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="download-dialog-action">
          <p className="data-ready">{assetsSelected.length} Assets selected</p>
          <Button
            role="dialogButton"
            icon={'pi pi-cog'}
            label={'Download'}
            className="p-button p-component load-button"
            disabled={!assetsSelected.length}
            onClick={handleClickOnDownload}
          />
        </div>
      </div>
    </Dialog>
  );
};
